import { useCallback, useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetValueChainPositionsQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetBoardColumnNamesQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type { IGetValueChainPositionsResponse } from 'store/slices/dataSets/interfaces/IGetValueChainPositionsResponse';
import type { IValueChainPositions } from 'store/slices/dataSets/interfaces/IValueChainPositions';
import type IGetBoardColumnNamesResponseItem from 'store/slices/valueChainMap/interfaces/IGetBoardColumnNamesResponseItem';

const useValueChainPosition = (category?: string, valueChainMapId?: string) => {
  const { data: valueChainPositionsData, isLoading: isValueChainPositionsLoading } = useGetValueChainPositionsQuery();
  const {
    data: boardColumnNamesData,
    isLoading: isBoardColumnNamesLoading,
  } = useGetBoardColumnNamesQuery(valueChainMapId ? { valueChainMapId } : skipToken);
  const [vcOptionsList, setVcOptionsList] = useState<IOption[]>([]);
  const [listPosition, setListPosition] = useState<IOption[]>([]);

  const [columnsData, setColumnsData] = useState<IGetValueChainPositionsResponse>();

  const handleFormatColumnsData = (
    boardColumnNamesDataItems: IGetBoardColumnNamesResponseItem[],
  ): IGetValueChainPositionsResponse => {
    const groupedData: IValueChainPositions = {
      Upstream: [],
      'Own Operations': [],
      Downstream: [],
    };

    boardColumnNamesDataItems.forEach((item) => {
      const [categoryName, ...subcategoryParts] = item.columnName.split('.');
      const subcategory = subcategoryParts.join('.').trim();

      if (categoryName && subcategory) {
        const categoryKey = categoryName.trim() as keyof IValueChainPositions;
        if (groupedData[categoryKey]) {
          groupedData[categoryKey].push(subcategory);
        }
      }
    });

    return {
      data: groupedData,
    };
  };

  useEffect(() => {
    if (valueChainPositionsData && !valueChainMapId) {
      setColumnsData(valueChainPositionsData);
    }

    if (boardColumnNamesData && valueChainMapId) {
      const formattedColumnsData = handleFormatColumnsData(boardColumnNamesData?.data);
      setColumnsData(formattedColumnsData);
    }
  }, [valueChainPositionsData, valueChainMapId, boardColumnNamesData]);

  useEffect(() => {
    if (columnsData?.data) {
      const options = Object.entries(columnsData?.data).map(([key]) => ({
        value: key,
        label: key,
      }));
      setVcOptionsList(options);
    }
  }, [columnsData?.data]);

  const handleGetVcOptions = useCallback(
    (valueChainSection: string) => {
      const selectedOption = columnsData?.data[valueChainSection as keyof IValueChainPositions];
      if (selectedOption) {
        const listPositionOptions = selectedOption.map((option) => ({
          value: option,
          label: option,
        }));
        setListPosition(listPositionOptions);
      }
    },
    [columnsData?.data],
  );

  useEffect(() => {
    if (category) {
      handleGetVcOptions(category);
    }
  }, [category, handleGetVcOptions]);

  return {
    valueChainPositionsData: columnsData?.data,
    isValueChainPositionsLoading,
    isBoardColumnNamesLoading,
    vcOptionsList,
    handleGetVcOptions,
    listPosition,
    columnsList: boardColumnNamesData?.data || [],
  };
};

export default useValueChainPosition;
